import {
  MenuItem,
  Avatar,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  SxProps,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { AccordionArrowPurple, Checkmark, CheckmarkChecked } from 'Images';
import { ReactNode, useState } from 'react';

interface IControlledSelectCheckmark {
  options: string[];
  selected: string[];
  onChange: (event: SelectChangeEvent<string[]>, child: ReactNode) => void;
  label?: string;
  size: 'medium' | 'small';
  width?: string;
  renderValue?: (value: string[]) => ReactNode;
  maxHeight?: number;
  startAdornment?: ReactNode;
  displayEmpty?: boolean;
  ariaLabel?: string;
  disablePortal?: boolean;
  checkboxSx?: SxProps;
  menuPropsSx?: SxProps;
  formSx?: SxProps;
  paperSx?: SxProps;
  iconSx?: SxProps;
  selectSx?: SxProps;
  placeholder?: string;
  selectedText?: string;
  closeIcon?: string;
  ariaDescription?: string;
}

export const ControlledSelectCheckmark = ({
  onChange,
  options,
  selected,
  label,
  size,
  width,
  maxHeight,
  renderValue,
  displayEmpty,
  startAdornment,
  ariaLabel,
  disablePortal,
  checkboxSx,
  menuPropsSx,
  formSx,
  paperSx,
  iconSx,
  selectSx,
  placeholder,
  selectedText,
  closeIcon,
  ariaDescription,
}: IControlledSelectCheckmark) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  return (
    <FormControl
      sx={{
        width: width || '100%',
        bgcolor: '#fff',
        borderRadius: '8px',

        '&&& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '8px',
          borderWidth: '1px',
          borderColor: 'primary.shade_200',
        },

        '&&& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },

        '& label[data-shrink="false"]': { mt: '-5px' },
        ...formSx,
      }}
    >
      <InputLabel id='select-label-id'>{label}</InputLabel>
      <Select
        inputProps={{
          'aria-label': ariaLabel,
          'aria-expanded': isOpen,
          'aria-describedby': `${label || ariaLabel}-description`,
        }}
        multiple
        size={size}
        displayEmpty={displayEmpty || !!placeholder}
        value={selected}
        onChange={onChange}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        startAdornment={startAdornment}
        sx={{
          zIndex: 1,
          borderRadius: '8px',
          '&&&:hover': {
            fieldset: {
              borderColor: 'primary.main',
            },
          },
          '.MuiSelect-select': {
            fontWeight: 400,
          },
          '& .MuiSelect-icon': {
            cursor: 'pointer',
            top: 'auto',
          },
          '.MuiSelect-iconOpen': {
            top: 'auto',
          },

          ...selectSx,
        }}
        renderValue={(selected) =>
          selected.length === 0 && placeholder
            ? placeholder
            : renderValue
            ? renderValue(selected)
            : selectedText ||
              `${
                Dictionary[DICTIONARY.MedicalReferrals].search.advancedSearch
                  .chosen
              } ${selected.length}`
        }
        MenuProps={{
          disablePortal: disablePortal,
          sx: {
            ...menuPropsSx,
            zIndex: 5000,
          },
          PaperProps: {
            role: 'group',
            sx: {
              width: startAdornment ? width : undefined,
              color: 'text.secondary',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0px 4px 24px rgba(45, 112, 217, 0.16)',
              padding: '0',
              maxHeight: maxHeight,
              '& > ul': {
                padding: 0,
              },
              ...paperSx,
            },
          },
        }}
        IconComponent={(props) => (
          <Avatar
            {...props}
            src={closeIcon && isOpen ? closeIcon : AccordionArrowPurple}
            alt=''
            sx={{
              transform:
                closeIcon && isOpen ? 'rotate(0deg) !important' : undefined,
              width: closeIcon && isOpen ? '28px' : undefined,
              height: closeIcon && isOpen ? '28px' : undefined,
              mr: closeIcon && isOpen ? '6px' : undefined,
              mt: closeIcon && isOpen ? '2px' : undefined,
              ...iconSx,
            }}
          />
        )}
      >
        {options.map((option: string, index: number) => {
          const labelId = `${option.trim().replace(' ', '-')}-label`;
          return (
            <MenuItem
              key={index}
              value={option}
              sx={{
                zIndex: 5000,
                padding: '6px 16px 6px 10px',
                '&:hover': { bgcolor: 'primary.shade_100' },
                '&.Mui-selected': {
                  fontWeight: '400 !important',
                  bgcolor: 'primary.shade_100',
                },
              }}
            >
              <Checkbox
                disabled
                aria-labelledby={labelId}
                id={option.trim().replace(' ', '-')}
                checked={selected.indexOf(option) > -1}
                sx={checkboxSx}
                icon={
                  <Avatar
                    alt={''}
                    src={Checkmark}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checkedIcon={
                  <Avatar
                    alt={''}
                    src={CheckmarkChecked}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
              />
              <ListItemText
                id={labelId}
                primaryTypographyProps={{
                  fontWeight: {
                    xs: selected.indexOf(option) > -1 ? 700 : 400,
                    sm: selected.indexOf(option) > -1 ? 700 : 400,
                  },
                }}
                primary={option}
              />
            </MenuItem>
          );
        })}
      </Select>
      <span className='sr-only' id={`${label || ariaLabel}-description`}>
        {ariaDescription}
      </span>
    </FormControl>
  );
};
